import { useCallback } from 'react';
import snakecaseKeys from 'snakecase-keys';
import { useInvalidateQueries } from '@leagueplatform/core';
import {
  GET_USER_PROFILE_MESSAGE_TYPE,
  setUserProfile,
  type UserProfile,
} from '@leagueplatform/user-profile-api';
import { useMutation } from '@tanstack/react-query';

export interface Options {
  onSuccess?: () => void;
  onError?: () => void;
}

export const useUpdateUserProfile = (options?: Options) => {
  const invalidateQueries = useInvalidateQueries({
    queryKey: [GET_USER_PROFILE_MESSAGE_TYPE],
  });
  const { onSuccess, onError } = options || {};

  const { mutateAsync: updateUserProfileMutation, isLoading } = useMutation({
    mutationFn: (userProfile: Partial<UserProfile>) =>
      setUserProfile(userProfile),
    onSuccess: () => {
      invalidateQueries();
      if (onSuccess) onSuccess();
    },
    onError: () => {
      if (onError) onError();
    },
  });

  const updateUserProfile = useCallback(
    async (userProfile: Record<string, unknown>) => {
      // setUserProfile's SDK type is Partial<UserProfile>, but onboarding
      // sends an extended field not in that surface (the phoneNumbers
      // array, serialized to phone_numbers). Cast at this single SDK
      // boundary instead of leaking the assertion to every call site.
      const serializedBody = snakecaseKeys(
        userProfile,
      ) as unknown as Partial<UserProfile>;
      await updateUserProfileMutation(serializedBody);
    },
    [updateUserProfileMutation],
  );

  return { updateUserProfile, isLoading };
};
